import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [showContactModal, setShowContactModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    date: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');
    
    try {
      // Validar longitud del mensaje
      if (formData.message.length > 500) {
        setSubmitStatus('error');
        return;
      }

      // Preparar datos del formulario usando formato application/x-www-form-urlencoded
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('service', formData.service);
      formDataToSend.append('date', formData.date);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d3dkeurbr3iihosg1bsg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          date: '',
          message: ''
        });
        setTimeout(() => {
          setShowContactModal(false);
          setSubmitStatus('');
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(''), 5000);
    }
  };

  return (
    <>
      <footer className="bg-gradient-to-b from-gray-900/80 to-black border-t border-gold-500/30 py-8 sm:py-12 md:py-16 px-3 sm:px-4 md:px-6 relative">
        {/* Fondo estrellado sutil */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-pulse opacity-10"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${3 + Math.random() * 2}s`
              }}
            >
              <div className="w-0.5 h-0.5 rounded-full bg-gold-500"></div>
            </div>
          ))}
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-12">
            {/* Logo y descripción */}
            <div className="sm:col-span-2 lg:col-span-2">
              <div className="mb-4 sm:mb-6">
                <div className="mb-4">
                  <img 
                    src="https://static.readdy.ai/image/d7e77f925f0119a01fe5d8699c3ff655/bcce1cda134b8bda0e0ca09aeb53adfd.png" 
                    alt="Starlight Christmas" 
                    className="w-40 sm:w-48 md:w-56 h-auto mx-auto sm:mx-0"
                  />
                  <p className="text-xs sm:text-sm text-gray-400 text-center sm:text-left mt-2 font-sans">
                    by Idoel Photography
                  </p>
                </div>
                <p className="text-sm sm:text-base text-gray-400 leading-relaxed font-sans text-center sm:text-left">
                  Capturamos la magia navideña en cada momento especial. Creamos recuerdos que brillarán para siempre en tu corazón.
                </p>
              </div>

              {/* Redes sociales */}
              <div className="text-center sm:text-left">
                <h4 className="text-base sm:text-lg md:text-xl font-semibold mb-3 sm:mb-4 text-gold-500 font-serif">Síguenos</h4>
                <div className="flex justify-center sm:justify-start space-x-3 sm:space-x-4">
                  {[
                    { icon: 'ri-instagram-fill', color: 'cherry', link: 'https://www.instagram.com/idoelphotography' },
                    { icon: 'ri-facebook-fill', color: 'gold', link: 'http://facebook.com/idoelphotography' },
                    { icon: 'ri-at-line', color: 'cherry', link: 'https://www.threads.com/@idoelphotography' }
                  ].map((social, index) => (
                    <a 
                      key={index} 
                      href={social.link}
                      target={social.link !== '#' ? '_blank' : '_self'}
                      rel={social.link !== '#' ? 'noopener noreferrer' : undefined}
                      className={`w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 flex items-center justify-center bg-gray-800/50 border rounded-xl transition-all duration-300 cursor-pointer hover:text-black touch-manipulation ${
                        social.color === 'gold' ? 'border-gold-500/50 hover:bg-gold-500' :
                        social.color === 'cherry' ? 'border-cherry-500/50 hover:bg-cherry-500' :
                        'border-pine-500/50 hover:bg-pine-500'
                      }`}
                    >
                      <i className={`${social.icon} text-base sm:text-lg md:text-xl`}></i>
                    </a>
                  ))}
                </div>
              </div>

            </div>

            {/* Enlaces rápidos */}
            <div className="text-center sm:text-left">
              <h4 className="text-base sm:text-lg md:text-xl font-semibold mb-4 sm:mb-6 text-gold-500 font-serif">Enlaces Rápidos</h4>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <a 
                    href="#hero-section" 
                    onClick={(e) => {
                      e.preventDefault();
                      const heroSection = document.getElementById('hero-section');
                      if (heroSection) {
                        heroSection.scrollIntoView({ 
                          behavior: 'smooth',
                          block: 'start'
                        });
                      }
                    }}
                    className="text-sm sm:text-base text-gray-400 hover:text-gold-400 transition-colors cursor-pointer font-sans"
                  >
                    Inicio
                  </a>
                </li>
                <li>
                  <Link 
                    to="/scenarios"
                    className="text-sm sm:text-base text-gray-400 hover:text-gold-400 transition-colors cursor-pointer font-sans"
                  >
                    Escenarios
                  </Link>
                </li>
                <li>
                  <a
                    href="#services-section"
                    onClick={(e) => {
                      e.preventDefault();
                      const element = document.getElementById('services-section');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    className="text-sm sm:text-base text-gray-400 hover:text-gold-400 transition-colors cursor-pointer font-sans"
                  >
                    Reservas
                  </a>
                </li>
                <li>
                  <button 
                    onClick={() => setShowContactModal(true)}
                    className="text-sm sm:text-base text-gray-400 hover:text-gold-400 transition-colors cursor-pointer font-sans touch-manipulation"
                  >
                    Contacto
                  </button>
                </li>
              </ul>
            </div>

            {/* Información de contacto */}
            <div className="text-center sm:text-left">
              <h4 className="text-base sm:text-lg md:text-xl font-semibold mb-4 sm:mb-6 text-gold-500 font-serif">Contacto</h4>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center justify-center sm:justify-start space-x-3">
                  <i className="ri-phone-fill text-base sm:text-lg flex-shrink-0 text-gold-500"></i>
                  <span className="text-sm sm:text-base text-gray-400 font-sans">787-459-9755</span>
                </div>
                <div className="flex items-center justify-center sm:justify-start space-x-3">
                  <i className="ri-mail-fill text-base sm:text-lg flex-shrink-0 text-gold-500"></i>
                  <span className="text-xs sm:text-sm md:text-base text-gray-400 break-all font-sans">info@idoelphotography.com</span>
                </div>
                <div className="flex items-start justify-center sm:justify-start space-x-3">
                  <i className="ri-map-pin-fill text-base sm:text-lg flex-shrink-0 mt-0.5 text-gold-500"></i>
                  <span className="text-sm sm:text-base text-gray-400 font-sans text-center sm:text-left">Urb. Valles de Patillas<br />Patillas, PR</span>
                </div>
              </div>
            </div>
          </div>

          {/* Métodos de pago */}
          <div className="border-t border-gold-500/30 mt-6 sm:mt-8 md:mt-12 pt-4 sm:pt-6 md:pt-8 text-center">
            <p className="text-xs sm:text-sm md:text-base text-gray-400 mb-2 font-sans">
              Aceptamos: Efectivo • Tarjetas de Crédito • PayPal
            </p>
            <p className="text-xs sm:text-sm md:text-base mb-4 font-sans">
              <span className="font-semibold text-gold-500">Compra ahora, paga después</span>
              <br />
              Financiamiento 0% con Klarna y Affirm
            </p>
          </div>

          {/* Copyright */}
          <div className="border-t border-gold-500/30 mt-4 sm:mt-6 md:mt-8 pt-4 sm:pt-6 md:pt-8 flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0">
            <p className="text-xs sm:text-sm text-gray-500 font-sans text-center sm:text-left">
              © 2025 Idoel Photography/ Starlight Christmas Photography/ Todos los derechos reservados.
            </p>
            <a 
              href="https://www.idoel.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs sm:text-sm text-gray-500 hover:text-gold-400 transition-colors cursor-pointer font-sans"
            >
              Diseño Web: Idoel Photography
            </a>
          </div>

        </div>
      </footer>

      {/* Modal de contacto - Completamente responsivo */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4" style={{ zIndex: 999999 }} onClick={() => setShowContactModal(false)}>
          <div className="bg-gradient-to-b from-gray-900/90 to-gray-800/90 backdrop-blur-sm border border-gold-500/30 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto relative" style={{ zIndex: 1000000 }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold font-serif text-gold-500">
                Ver Espacios Disponibles Mágica
              </h3>
              <button 
                onClick={() => setShowContactModal(false)}
                className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-full bg-gray-700/50 hover:bg-gray-600/50 transition-colors cursor-pointer touch-manipulation"
              >
                <i className="ri-close-line text-gray-300 text-sm sm:text-base"></i>
              </button>
            </div>

            <p className="text-gray-300 text-xs sm:text-sm mb-4 sm:mb-6 font-sans">
              Contáctame por Whatsapp para crear recuerdos navideños únicos e inolvidables. Estamos aquí para hacer realidad tu visión.
            </p>

            <form id="contact-form-modal" data-readdy-form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4 md:space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                <div>
                  <label className="block font-medium mb-1 sm:mb-2 text-sm sm:text-base text-gold-500 font-sans">Nombre Completo</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-gray-800/50 border border-gray-600/50 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2 sm:py-3 text-white focus:outline-none focus:border-gold-500 transition-colors text-sm font-sans"
                    placeholder="Tu nombre completo"
                  />
                </div>
                <div>
                  <label className="block font-medium mb-1 sm:mb-2 text-sm sm:text-base text-gold-500 font-sans">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-gray-800/50 border border-gray-600/50 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2 sm:py-3 text-white focus:outline-none focus:border-gold-500 transition-colors text-sm font-sans"
                    placeholder="tu@email.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                <div>
                  <label className="block font-medium mb-1 sm:mb-2 text-sm sm:text-base text-gold-500 font-sans">Teléfono</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full bg-gray-800/50 border border-gray-600/50 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2 sm:py-3 text-white focus:outline-none focus:border-gold-500 transition-colors text-sm font-sans"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block font-medium mb-1 sm:mb-2 text-sm sm:text-base text-gold-500 font-sans">Tipo de Sesión</label>
                  <div className="relative">
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-gray-800/50 border border-gray-600/50 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2 sm:py-3 text-white focus:outline-none focus:border-gold-500 transition-colors text-sm pr-8 appearance-none cursor-pointer font-sans"
                    >
                      <option value="">Selecciona un servicio</option>
                      <option value="classic">Classic Christmas</option>
                      <option value="magic">Magic Christmas</option>
                      <option value="starlight">Starlight VIP</option>
                    </select>
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <i className="ri-arrow-down-s-line text-gray-400"></i>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block font-medium mb-1 sm:mb-2 text-sm sm:text-base text-gold-500 font-sans">Fecha Preferida</label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  className="w-full bg-gray-800/50 border border-gray-600/50 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2 sm:py-3 text-white focus:outline-none focus-border-gold-500 transition-colors text-sm font-sans"
                />
              </div>

              <div>
                <label className="block font-medium mb-1 sm:mb-2 text-sm sm:text-base text-gold-500 font-sans">Mensaje</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={3}
                  maxLength={500}
                  className="w-full bg-gray-800/50 border border-gray-600/50 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2 sm:py-3 text-white focus:outline-none focus:border-gold-500 transition-colors text-sm resize-none font-sans"
                  placeholder="Cuéntanos sobre tu visión para la sesión..."
                ></textarea>
                <div className="text-right text-gray-400 text-xs mt-1 font-sans">
                  {formData.message.length}/500 caracteres
                </div>
              </div>

              {/* Estado del envío */}
              {submitStatus === 'success' && (
                <div className="bg-pine-500/20 border border-pine-500/50 rounded-lg sm:rounded-xl p-3 sm:p-4 text-pine-300 text-sm font-sans">
                  ¡Mensaje enviado exitosamente! Te contactaremos pronto.
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="bg-cherry-500/20 border border-cherry-500/50 rounded-lg sm:rounded-xl p-3 sm:p-4 text-cherry-300 text-sm font-sans">
                  Error al enviar el mensaje. Por favor, inténtalo de nuevo.
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting || formData.message.length > 500}
                className="w-full text-black font-semibold py-2.5 sm:py-3 md:py-4 px-4 sm:px-6 rounded-lg sm:rounded-xl transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base bg-gradient-to-r from-gold-500 to-gold-600 hover:from-gold-600 hover:to-gold-700 font-sans touch-manipulation"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Enviando...
                  </span>
                ) : (
                  'Enviar Mensaje'
                )}
              </button>

            </form>
          </div>
        </div>
      )}
    </>
  );
}
